import { useContext, useEffect, useState } from "react";
import { Button, Flex } from "antd";

import { UserContext } from "@/context/UserContext";

import { getPermission, requestPermission } from "../permission";
import { dismiss, isDismissed, isSnoozed, snooze } from "../promptState";
import { detectBrowser } from "../browser";
import DeniedHint from "../DeniedHint";
import styles from "./browser-notify-prompt.module.css";

const SHOW_DELAY_MS = 2000;

/**
 * Приглашение включить системные уведомления о новых сообщениях.
 * Показывается, пока браузер не спрашивал разрешение (permission = "default")
 * и пользователь не нажал «Не сейчас» в последние 7 дней.
 */
export default function BrowserNotifyPrompt({ config }) {
  const { user } = useContext(UserContext) || {};
  const userUuid = user?.user_uuid || user?.uuid;
  const [view, setView] = useState(null); // null | "prompt" | "blocked"
  const [blockedBrowser, setBlockedBrowser] = useState("unknown");

  useEffect(() => {
    if (!config?.messenger_enabled || !userUuid) {
      setView(null);
      setBlockedBrowser("unknown");
      return undefined;
    }

    const permission = getPermission();

    if (permission === "denied") {
      const dismissed = isDismissed(userUuid);
      setBlockedBrowser(dismissed ? "unknown" : detectBrowser());
      setView(dismissed ? null : "blocked");
      return undefined;
    }

    if (permission !== "default" || isSnoozed(userUuid)) {
      setView(null);
      setBlockedBrowser("unknown");
      return undefined;
    }

    const timer = setTimeout(() => setView("prompt"), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [config?.messenger_enabled, userUuid]);

  const handleEnable = async () => {
    const result = await requestPermission();
    if (result === "denied") {
      if (isDismissed(userUuid)) {
        setBlockedBrowser("unknown");
        setView(null);
      } else {
        setBlockedBrowser(detectBrowser());
        setView("blocked");
      }
      return;
    }
    if (result !== "granted") {
      // Окно запроса закрыли без ответа — спросим позже.
      snooze(userUuid);
    }
    setView(null);
  };

  const handleLater = () => {
    snooze(userUuid);
    setView(null);
  };

  const handleDeniedClose = () => {
    dismiss(userUuid);
    setBlockedBrowser("unknown");
    setView(null);
  };

  if (view === "blocked") {
    return (
      <div className={styles.wrapper}>
        <DeniedHint browser={blockedBrowser} onClose={handleDeniedClose} />
      </div>
    );
  }

  if (view !== "prompt") {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.card} role="dialog" aria-live="polite">
        <div className={styles.title}>
          Включить уведомления о новых сообщениях?
        </div>
        <div className={styles.text}>
          Браузер покажет новое сообщение, даже когда вкладка TeamTools в фоне.
        </div>
        <Flex gap={8} justify="flex-end" className={styles.actions}>
          <Button size="small" onClick={handleLater}>
            Не сейчас
          </Button>
          <Button type="primary" size="small" onClick={handleEnable}>
            Включить
          </Button>
        </Flex>
      </div>
    </div>
  );
}
